/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CustomApplicationFormView.tsx
 🗓️ Created: 2026-01-16
 🧩 Description:
    Custom Application form view - follows Extensions pattern.
    Full page form with ModuleFormHeader, FormBox sections,
    4-column grid layout, and FixedActionBar.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Code, Settings, FileCode, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Toggle } from '../../components/ui/Toggle';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import {
    getCustomApplication,
    createCustomApplication,
    updateCustomApplication,
    deleteCustomApplication,
    listCustomApplications
} from './api/customApplicationsApi';
import { CustomApplication, CreateCustomApplicationPayload } from './types';
import { getDefaultScript } from './utils/customAppDefaults';
import { CustomAppGeneralTab } from './components/CustomAppGeneralTab';
import { CustomAppScriptTab } from './components/CustomAppScriptTab';

export default function CustomApplicationFormView() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const duplicateId = searchParams.get('duplicate');
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const isEditMode = id && id !== 'new';

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    const [appType, setAppType] = useState<'lua' | 'xml'>('lua');
    const [scriptContent, setScriptContent] = useState('');
    const [finalDestinationModule, setFinalDestinationModule] = useState('');
    const [finalDestinationValue, setFinalDestinationValue] = useState('');
    const [skipFinalDestination, setSkipFinalDestination] = useState(false);
    const [enabled, setEnabled] = useState(true);

    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Quick Search state
    const [showQuickList, setShowQuickList] = useState(false);
    const [quickListSearch, setQuickListSearch] = useState('');
    const [quickListApps, setQuickListApps] = useState<CustomApplication[]>([]);
    const [initialApps, setInitialApps] = useState<CustomApplication[]>([]);
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const [isQuickSearching, setIsQuickSearching] = useState(false);
    const [activeTab, setActiveTab] = useState('general');

    // Original data for change detection
    const [originalData, setOriginalData] = useState<CustomApplication | null>(null);

    // Load application data in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            setIsLoading(true);
            getCustomApplication(id)
                .then(response => {
                    if (response.success && response.data) {
                        const app = response.data;
                        setName(app.name);
                        setDescription(app.description || '');

                        setAppType(app.appType);
                        setScriptContent(app.scriptContent || '');
                        setFinalDestinationModule(app.finalDestinationModule || '');
                        setFinalDestinationValue(app.finalDestinationValue || '');
                        setSkipFinalDestination(app.skipFinalDestination);
                        setEnabled(app.enabled);
                        setOriginalData(app);
                    }
                })
                .catch(err => {
                    console.error('Error loading application:', err);
                    showError(t('customApplications.messages.loadError'));
                })
                .finally(() => setIsLoading(false));
        }
    }, [id, isEditMode, showError, t]);

    // Detect changes
    useEffect(() => {
        if (!originalData) {
            setHasChanges(
                name.trim() !== '' ||
                description.trim() !== '' ||
                scriptContent.trim() !== ''
            );
        } else {
            setHasChanges(
                name !== originalData.name ||
                description !== (originalData.description || '') ||
                appType !== originalData.appType ||
                scriptContent !== (originalData.scriptContent || '') ||
                finalDestinationModule !== (originalData.finalDestinationModule || '') ||
                finalDestinationValue !== (originalData.finalDestinationValue || '') ||
                skipFinalDestination !== originalData.skipFinalDestination ||
                enabled !== originalData.enabled
            );
        }
    }, [name, description, appType, scriptContent, finalDestinationModule, finalDestinationValue, skipFinalDestination, enabled, originalData]);

    // Navigation handlers
    const handleGoToList = useCallback(() => {
        navigate('/pbx/applications/custom-applications');
    }, [navigate]);

    // Navigate to new
    const handleAddNew = useCallback(() => {
        navigate('/pbx/applications/custom-applications/new');
    }, [navigate]);

    // Save handler
    const handleSave = useCallback(async () => {
        // Validate
        if (!name.trim()) {
            showError(t('customApplications.form.name') + ' ' + t('common.required'));
            return undefined;
        }


        setIsSaving(true);
        try {
            const payload: CreateCustomApplicationPayload = {
                name: name.trim(),
                description: description.trim() || undefined,

                appType,
                scriptContent: scriptContent || undefined,
                finalDestinationModule: skipFinalDestination ? undefined : finalDestinationModule || undefined,
                finalDestinationValue: skipFinalDestination ? undefined : finalDestinationValue || undefined,
                skipFinalDestination,
                enabled
            };

            let result;
            if (isEditMode && id) {
                result = await updateCustomApplication(id, payload);
            } else {
                result = await createCustomApplication(payload);
            }

            if (result.success) {
                showSuccess(isEditMode
                    ? t('customApplications.messages.updateSuccess')
                    : t('customApplications.messages.createSuccess')
                );
                setOriginalData(result.data);
                return result.data;
            } else {
                showError((result as unknown as { message?: string })?.message || t('customApplications.messages.saveError'));
                return undefined;
            }
        } catch (err) {
            console.error('Failed to save application:', err);
            showError(t('customApplications.messages.saveError'));
            return undefined;
        } finally {
            setIsSaving(false);
        }
    }, [name, description, appType, scriptContent, finalDestinationModule, finalDestinationValue, skipFinalDestination, enabled, isEditMode, id, showSuccess, showError, t]);

    // Save with action (for FixedActionBar dropdown)
    const handleSaveWithAction = useCallback(async (action: 'save' | 'save-and-close' | 'save-and-new') => {
        const savedData = await handleSave();
        if (!savedData) return;

        switch (action) {
            case 'save-and-close':
                navigate('/pbx/applications/custom-applications');
                break;
            case 'save-and-new':
                navigate('/pbx/applications/custom-applications/new');
                break;
            case 'save':
            default:
                if (!isEditMode && savedData.uuid) {
                    navigate(`/pbx/applications/custom-applications/${savedData.uuid}`);
                }
                break;
        }
    }, [handleSave, navigate, isEditMode]);

    // Cancel handler
    const handleCancel = useCallback(() => {
        navigate('/pbx/applications/custom-applications');
    }, [navigate]);

    // Delete handlers
    const handleDeleteClick = useCallback(() => {
        if (!isEditMode || !id) return;
        setShowDeleteModal(true);
    }, [isEditMode, id]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!isEditMode || !id) return;

        setIsDeleting(true);
        try {
            await deleteCustomApplication(id);
            showSuccess(t('customApplications.messages.deleteSuccess'));
            navigate('/pbx/applications/custom-applications');
        } catch (error) {
            showError(error.message || t('customApplications.messages.deleteError'));
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    }, [isEditMode, id, showSuccess, showError, t, navigate]);

    // Quick List loader
    const loadInitialApps = useCallback(async () => {
        if (initialApps.length > 0) return;

        setIsLoadingInitial(true);
        try {
            const result = await listCustomApplications({
                page: 1,
                limit: 100,
                sortBy: 'name',
                sortOrder: 'asc'
            });
            setInitialApps(result.data);
            setQuickListApps(result.data);
        } catch (error) {
            console.error('Error loading initial apps:', error);
            setInitialApps([]);
            setQuickListApps([]);
        } finally {
            setIsLoadingInitial(false);
        }
    }, [initialApps.length]);

    const loadQuickList = useCallback(async (search: string) => {
        const normalized = search.trim();

        if (!normalized || normalized.length < 2) {
            setQuickListApps(initialApps);
            return;
        }

        setIsQuickSearching(true);
        try {
            const result = await listCustomApplications({
                page: 1,
                limit: 50,
                sortBy: 'name',
                sortOrder: 'asc',
                search: normalized
            });
            setQuickListApps(result.data);
        } catch (error) {
            console.error('Error loading quick list apps:', error);
            setQuickListApps([]);
        } finally {
            setIsQuickSearching(false);
        }
    }, [initialApps]);

    // Load initial apps when Quick List modal opens
    useEffect(() => {
        if (showQuickList && initialApps.length === 0) {
            loadInitialApps();
        }
    }, [showQuickList, initialApps.length, loadInitialApps]);

    // Debounced Quick List search
    useEffect(() => {
        if (!showQuickList) return;

        const timer = setTimeout(() => {
            if (quickListSearch.trim().length >= 2) {
                loadQuickList(quickListSearch);
            } else {
                // Restore initial apps if search is cleared
                setQuickListApps(initialApps);
            }
        }, 800);

        return () => clearTimeout(timer);
    }, [quickListSearch, showQuickList, loadQuickList, initialApps]);

    // Duplicate handler
    const handleDuplicateFromForm = useCallback(() => {
        if (!isEditMode || !id) return;
        navigate(`/pbx/applications/custom-applications/new?duplicate=${id}`);
    }, [isEditMode, id, navigate]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Code className="h-5 w-5 text-primary" />}
                title={isEditMode ? `${t('customApplications.form.editTitle')} - ${name || ''}` : t('customApplications.form.createTitle')}
                backButtonText={t('customApplications.actions.list')}
                onBack={handleGoToList}
                actions={[
                    // 1. Quick Search (always visible)
                    {
                        icon: <Search className="h-5 w-5 text-muted-foreground" />,
                        tooltip: t('common.quickSearch'),
                        onClick: () => setShowQuickList(true)
                    },
                    // 2. Duplicate (edit mode only)
                    ...(isEditMode
                        ? [{
                            icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                            tooltip: t('common.duplicate'),
                            onClick: handleDuplicateFromForm
                        }]
                        : []),
                    // 3. Add New (always visible)
                    {
                        icon: <Plus className="h-5 w-5 text-muted-foreground" />,
                        tooltip: t('customApplications.actions.add'),
                        onClick: handleAddNew,
                    }
                ]}
            />

            <div className="mt-8">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="general" icon={<Settings className="h-4 w-4" />}>
                            {t('customApplications.tabs.general')}
                        </TabsTrigger>
                        <TabsTrigger value="script" icon={<FileCode className="h-4 w-4" />}>
                            {t('customApplications.tabs.script')}
                        </TabsTrigger>
                    </TabsList>

                    {/* General Settings Tab */}
                    <TabsContent value="general">
                        <CustomAppGeneralTab
                            name={name}
                            description={description}
                            appType={appType}
                            scriptContent={scriptContent}
                            enabled={enabled}
                            skipFinalDestination={skipFinalDestination}
                            finalDestinationModule={finalDestinationModule}
                            finalDestinationValue={finalDestinationValue}
                            onNameChange={setName}
                            onDescriptionChange={setDescription}
                            onAppTypeChange={setAppType}
                            onScriptContentChange={setScriptContent}
                            onEnabledChange={setEnabled}
                            onSkipFinalDestinationChange={setSkipFinalDestination}
                            onFinalDestinationModuleChange={setFinalDestinationModule}
                            onFinalDestinationValueChange={setFinalDestinationValue}
                            t={t}
                        />
                    </TabsContent>

                                        {/* Script Content Tab */}
                    <TabsContent value="script">
                        <CustomAppScriptTab
                            appType={appType}
                            scriptContent={scriptContent}
                            onScriptContentChange={setScriptContent}
                            t={t}
                        />
                    </TabsContent>
                                </Tabs>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                onDelete={handleDeleteClick}
                isEditMode={!!isEditMode}
                isSaving={isSaving}
                cancelText={t('common.cancel')}
                deleteText={t('common.delete')}
                useSaveDropdown={true}
                saveDropdownStorageKey="customApplications:saveAction"
                saveDisabled={isSaving}
            />

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                title={t('customApplications.actions.delete')}
                message={`${t('customApplications.messages.deleteConfirm')} (${name})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
                isLoading={isDeleting}
            />

            {/* Quick Search Modal */}
            <QuickSearchModal
                isOpen={showQuickList}
                onClose={() => {
                    setShowQuickList(false);
                    setQuickListSearch('');
                }}
                title={t('customApplications.title')}
                items={quickListApps}
                isLoadingInitial={isLoadingInitial}
                isSearching={isQuickSearching}
                searchValue={quickListSearch}
                onSearchChange={setQuickListSearch}
                onItemSelect={(app) => {
                    setShowQuickList(false);
                    setQuickListSearch('');
                    navigate(`/pbx/applications/custom-applications/${app.uuid || app.id}`);
                }}
                currentItemId={id}
                renderItem={(app, isCurrent) => (
                    <button
                        onClick={() => {
                            if (!isCurrent) {
                                setShowQuickList(false);
                                setQuickListSearch('');
                                navigate(`/pbx/applications/custom-applications/${app.uuid || app.id}`);
                            }
                        }}
                        disabled={isCurrent}
                        className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
                            ? 'bg-primary/20 border-2 border-primary cursor-default'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
                            }`}
                    >
                        <div className="flex items-center gap-3">
                            <Code className="h-4 w-4 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-foreground">
                                        {app.name}
                                    </span>
                                    {isCurrent && (
                                        <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                                            {t('common.current', 'Current')}
                                        </span>
                                    )}
                                </div>
                                {app.description && (
                                    <div className="text-sm text-muted-foreground truncate">
                                        {app.description}
                                    </div>
                                )}
                            </div>
                        </div>
                    </button>
                )}
                searchPlaceholder={t('common.searchPlaceholder')}
                minCharsMessage={t('customApplications.quickListMinChars', 'Type at least 2 characters to search')}
                emptyMessage={t('customApplications.quickListEmpty', 'Start typing to search custom applications')}
                noResultsMessage={t('customApplications.quickListNoResults', 'No custom applications found')}
                headerIcon={<Code className="h-5 w-5" />}
                minSearchChars={2}
            />
        </div>
    );
}
